import { defineDataAccessConfig } from "@/flows/utils"
import { getEmptyAccount } from "@/models/account/account"
import { getEmptyContact } from "@/models/contacts/contact"
import { useCurrentAccountStore } from "@/stores/currentAccount"
import { useReviewPaymentStore } from "@/stores/reviewPayment"
import { useDeceasedHelper } from "@/flows/useCases/deceased/deceasedFlowHelper"
import { ReviewPeriodMutator } from "@/mutators/reviewPeriod/ReviewPeriodMutator"
import { CreditRefundMutator } from "@/mutators/creditRefund/CreditRefundMutator"
import { useSnackbarStore } from "@/stores/snackbar"
import { useImbpSaveContactNumberPromptStore } from "@/stores/imbpSaveContactNumberPrompt"
import { formatDate } from "@/helpers/dateHelper"
import { RouteNames } from "@/types/router"
import { THE_ESTATE_OF } from "@/constants/deceased"
import router from "@/plugins/router"
import dayjs from "dayjs"
import tariffHelper from "@/helpers/tariffHelper"
import { SmartPayAsYouGoMeterPoint } from "@/api/backofficeRest/generated/model"
import { errorHandler, errorReporter } from "@/services/ErrorService"
import { ImbpMutator } from "@/mutators/imbp/ImbpMutator"
import { QuestionnaireFormContent } from "@/flows/useCases/imbpBookingNew/types"
import { externalRoutes } from "@/constants/routes/externalRoutes"

/*
  Flow notes are audit side effects of an action that has already completed, so
  a note failure is reported but deliberately does not reject into the flow.
  Agent attribution is appended server-side, so it is not composed here.
*/
const addFlowNote = (summary: string, content: string) =>
  useCurrentAccountStore()
    .addNote()
    .mutateAsync({ subject: "General Note", summary, content })
    .catch((error: unknown) => {
      errorReporter.report(error)
    })

// Add getters and actions to be used in flow definitions
export default defineDataAccessConfig({
  getters: {
    current: {
      account: {
        getter: () => useCurrentAccountStore().account,
        empty: () => getEmptyAccount(),
      },
      latestRead: {
        getter: () => useCurrentAccountStore().latestRead,
        empty: () => ({ date: null, cumulative: 0 }),
      },
      today: {
        getter: () => new Date(),
      },
      primaryAccountContact: {
        getter: () => useCurrentAccountStore().primaryAccountContact,
        empty: () => getEmptyContact(),
      },
      accountContacts: {
        getter: () => useCurrentAccountStore().accountContacts,
        empty: () => [],
      },
    },
  },
  actions: {
    current: {
      submitReading: () => useCurrentAccountStore().submitReading(),
      issueBill: () => useCurrentAccountStore().submitIssueBill(),
      submitImbpBooking: (args) => {
        const { jobNotes, meterDetails, engineerAccess, ...rest } = args

        const note = jobNotes
          ? Object.fromEntries(
              Object.entries(jobNotes).filter(
                ([key]) => key !== "jobSelection" && key !== "extras",
              ),
            )
          : {}

        const questionnaire = Object.values<QuestionnaireFormContent>({
          ...note,
          ...meterDetails,
          ...engineerAccess,
        }).filter((item) => Boolean(item.answer))

        useCurrentAccountStore().submitImbpBooking({
          ...rest,
          questionnaire,
        })

        router.push({
          name: "CustomerImbpBookingComplete",
        })
      },
      reserveImbpSlot: (args) => useCurrentAccountStore().reserveImbpSlot(args),
      closeSidePanel: () =>
        useCurrentAccountStore().setSidePanelCollapsed(true),
      closeRefundReview: () => {
        useReviewPaymentStore().reset()
        router.push({ name: RouteNames.CustomerFinancials })
      },
    },
    cot: {
      submit: (args) => {
        const current = useCurrentAccountStore()
        const params = {
          ...args,
          isMovingIn: !!args.isMovingIn,
          primaryAccountContact: current.primaryAccountContact.data,
          account: current.account.data,
          meterpointTariffs: current.meterpointTariffs.data,
        }

        current.submitChangeOfTenancy().mutateAsync(params)
      },
    },
    smartPayAsYouGo: {
      submit: async (args) => {
        args.spaygRequest.forEach((request: SmartPayAsYouGoMeterPoint) => {
          if (!request.productRef) {
            errorReporter.report(
              new Error(
                `SPAYG submit: meterpoint ${request.meterPointIdentifier} has no productRef (empty smartPaygTariffs). Please resync the account with Junifer.`,
              ),
            )
          }
        })

        if (
          args.spaygRequest.some(
            (request: SmartPayAsYouGoMeterPoint) => !request.productRef,
          )
        ) {
          throw new Error(
            "We couldn't switch the selected meterpoints to Smart Pay As You Go. Please try again, or contact IT support if this keeps happening.",
          )
        }

        const isSingleMeterSetup = args.spaygRequest.length === 1

        const spaygRequest = args.spaygRequest.map(
          (request: SmartPayAsYouGoMeterPoint) => {
            const isGasProduct = tariffHelper.isGas(request.productRef)

            return {
              ...request,
              repaymentRate:
                isSingleMeterSetup || !isGasProduct
                  ? Number(args.spaygRepaymentAmount || 0).toFixed(2)
                  : undefined,
              source: args.switchSource,
            }
          },
        )
        const currentAccount = useCurrentAccountStore()
        await currentAccount.submitSmartPayAsYouGo().mutateAsync({
          billingAccountNumber: currentAccount.accountNumber,
          input: spaygRequest,
        })
      },
    },
    refund: {
      submitRefund: async (args) => {
        try {
          await CreditRefundMutator.creditRefund().mutateAsync(args.refund)
        } catch (error) {
          /*
            The flow manager keeps the agent on the form and shows the thrown
            message inline, so throw the same agent-facing text the snackbar
            shows; nothing below runs for a refused refund.
          */
          const message = errorHandler.userMessage(
            error,
            "Refund submission failed",
          )
          useSnackbarStore().addError(message)
          throw new Error(message, { cause: error })
        }

        // The backend notes the refund request itself.
        if (args.cantUpdatePaymentNote) {
          void addFlowNote(
            "Customer unable to update payment before a refund",
            `When processing a refund, the agent was prompted to ask the customer to update their payment schedule. Notes: ${args.cantUpdatePaymentNote}`,
          )
        }
        useReviewPaymentStore().reset()
        useSnackbarStore().addSuccess("Refund successfully submitted!")
        router.push({ name: RouteNames.CustomerFinancials })
      },
    },
    deceased: {
      pauseComms: () => {
        if (!useDeceasedHelper().isMeterRemindersDisabled().data) {
          useCurrentAccountStore().toggleMeterReminders(false)
        }
        if (!useDeceasedHelper().isCommsDisabled().data) {
          const primaryContact =
            useCurrentAccountStore().accountContacts.data.find(
              (contact) => contact.juniferPrimaryContact,
            )

          if (primaryContact) {
            void useCurrentAccountStore().updateContactComms().mutateAsync({
              contact: primaryContact,
              billDelivery: "None",
              receiveEmail: false,
              receivePost: false,
              receiveSMS: false,
            })
          }
        }
        if (!useDeceasedHelper().isAccountInReview().data) {
          /*
            Returned so the flow manager awaits it: a failure to create the
            review period must surface to the agent and stop the flow
            advancing, rather than floating as an unhandled rejection.
          */
          return ReviewPeriodMutator.addForAccount()
            .mutateAsync({
              reason: "COT Deceased",
              isBillingSuppressed: true,
              isDunningSuppressed: true,
              isPaymentCollectionSuppressed: true,
              fromDttm: dayjs(),
              toDttm: dayjs().add(2, "month"),
              description: `Account put in review on ${formatDate(
                dayjs(),
              )} for reason: 'COT Deceased'.`,
            })
            .then(() =>
              addFlowNote(
                "Review Added",
                `Put the account in review on ${formatDate(dayjs())}`,
              ),
            )
            .then(() => {
              useSnackbarStore().addSuccess(
                "Successfully created Review Period. It may take some time for the changes to take effect",
              )
            })
        }
      },
      gatherDetails: (args) => {
        if (!useDeceasedHelper().isActivityStreamNotified().data) {
          void addFlowNote(
            "Bereavement details",
            `${args.executorOfEstateOrPropertyInformation}`,
          )
        }
      },
      submitTicketNumber: (args) => {
        if (!useDeceasedHelper().hasSubmittedDeathCertTicketNumber().data) {
          void addFlowNote(
            "Death Certificate Received",
            `The death certificate has been sent and checked. You can find this on ticket number ${args.deathCertTicketNumber}`,
          )
        }
      },
      finishFlow: () => {
        function redirect() {
          void router.push({ name: RouteNames.CustomerChangeOfTenancy })
        }

        if (!useCurrentAccountStore().isCustomerDeceased.data) {
          const primary = useCurrentAccountStore().accountContacts.data.find(
            (contact) => contact.juniferPrimaryContact,
          )

          if (primary) {
            useCurrentAccountStore()
              .updateContact()
              .mutateAsync({
                contact: primary,
                edits: {
                  forename: THE_ESTATE_OF,
                  surname: `${primary.firstName + primary.lastName}`,
                },
              })
              .then(() => redirect())
          }
        } else {
          redirect()
        }
      },
    },
    reviewPayment: {
      useIndustryEstimates: () =>
        (useReviewPaymentStore().useUsageEstimatedMeterpoints = false),
      useUsageEstimates: () =>
        (useReviewPaymentStore().useUsageEstimatedMeterpoints = true),
      submitReviewPayment: () => useReviewPaymentStore().submit(),
    },
    imbp: {
      submitSmartMeterInterest: (args) => {
        try {
          const current = useCurrentAccountStore()
          const postcode = current.account.data.supplyAddress?.postcode
          if (!postcode || !current.coreId.data)
            throw new Error("Missing details for smart meter register")

          return ImbpMutator.submitSmartMeterInterest()
            .mutateAsync({
              billingAccountId: current.coreId.data.toString(),
              postcode: postcode.toUpperCase(),
              customerInterest: args.customerInterest,
            })
            .then(() => {
              useSnackbarStore().addSuccess(
                "Availability saved. The customer will receive an email when matching appointments become available.",
              )
              current.imbpSmartMeterInterest.refetch()
              router.push({ name: RouteNames.CustomerMeterpoints })
            })
        } catch (err) {
          errorReporter.report(err)
          useSnackbarStore().addError(
            "An error occurred when updating preferences",
          )
        }
      },
      updateGetAvailableSlots: (args) => {
        const current = useCurrentAccountStore()
        current.getImbpSlotWithCoreId =
          args.appointmentType === "SMART_METER_UPGRADE"
      },
      updateAppointmentTypeIfSmartMeterSelected: (args) => {
        const current = useCurrentAccountStore()
        if (args.jobNotes.jobSelection === "SMART_METER_UPGRADE") {
          args.appointmentType = "SMART_METER_UPGRADE"
        }
        current.getImbpSlotWithCoreId =
          args.appointmentType === "SMART_METER_UPGRADE"
      },
      removeCoreIdAndReserveSlot: (args) => {
        const current = useCurrentAccountStore()
        if (!args.appointmentData.noSuitableAppointments) {
          current.reserveImbpSlot({
            ...args,
            jobType: {
              selectedMeterTypeAes:
                current.smartMeterEligibility.data.meterInstallationType,
              selectedInstallationType: "SMETS2",
              selectedAppointmentType: "METER_EXCHANGE",
            },
          })
        }
        current.getImbpSlotWithCoreId = false
      },
      openSiteworksGuide: () => {
        window.open(externalRoutes.SiteworksTasksGuide.path, "_blank")
      },
      openMeterTroubleshootingGuide: () => {
        window.open(
          externalRoutes.MeteringTroubleshootingArticles.path,
          "_blank",
        )
      },
      offerToSaveContactNumber: (args) => {
        const contact = useCurrentAccountStore().imbpAccount.data?.contactInfo
        const phoneNumber =
          args.installationEssentials?.contactPhoneNumber?.trim() ?? ""
        const prompt = useImbpSaveContactNumberPromptStore()
        if (!contact || !prompt.shouldOffer(contact, phoneNumber)) return
        return prompt.ask(contact, phoneNumber)
      },
      combineJobNotes: (args) => {
        const current = useCurrentAccountStore()
        const notes: string[] = []

        const jobNotesAnswer = args.jobNotes?.note?.answer
        if (jobNotesAnswer) {
          notes.push(jobNotesAnswer)
        }

        const meterDetailsAnswer = args.meterDetails?.note?.answer
        if (meterDetailsAnswer) {
          notes.push(meterDetailsAnswer)
        }

        const vulnerabilities =
          current.customerSettings.data?.customerVulnerabilities || []

        if (vulnerabilities.length > 0) {
          const vulnerabilityLabels = vulnerabilities
            .map((vuln) => vuln.label)
            .join(", ")
          const vulnerabilityNote = `This customer or someone they live with has the following vulnerabilities: ${vulnerabilityLabels}`
          notes.push(vulnerabilityNote)
        }

        if (notes.length > 0) {
          args.jobNotes = {
            ...args.jobNotes,
            note: {
              question: "Important to note re the job?",
              answer: notes.join(". "),
            },
          }
        }

        if (args.meterDetails?.note) {
          delete args.meterDetails.note
        }
      },
    },
  },
})
